import { FC, useState, useEffect } from 'react'
import { Modal, Form, Input, Button, Alert, Typography, Result } from 'antd'
import { LockOutlined, MailOutlined, CloseOutlined, MailTwoTone } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import { PasswordStrengthIndicator } from '../ui/PasswordStrengthIndicator'
import './auth-modal.css'

const { Title, Text } = Typography

interface RegisterModalProps {
  visible: boolean
  onClose: () => void
  onSwitchToLogin: () => void
}

// 将Form组件分离出来，避免在Modal不可见时创建form实例
const RegisterForm: FC<{
  onSubmit: (values: any) => void
  loading: boolean
  password: string
  setPassword: (password: string) => void
  onSwitchToLogin: () => void
}> = ({ onSubmit, loading, password, setPassword, onSwitchToLogin }) => {
  const { t } = useTranslation(['common', 'auth'])
  const [form] = Form.useForm()

  return (
    <Form
      form={form}
      onFinish={onSubmit}
      layout="vertical"
      size="large"
    >
      <Form.Item
        name="email"
        label={t('auth:form.email')}
        rules={[
          { required: true, message: t('auth:validation.emailRequired') },
          { type: 'email', message: t('auth:validation.emailInvalid') }
        ]}
      >
        <Input
          prefix={<MailOutlined className="auth-modal__input-icon" />}
          placeholder={t('auth:placeholder.email')}
          autoComplete="email"
        />
      </Form.Item>

      <Form.Item
        name="password"
        label={t('auth:form.password')}
        rules={[
          { required: true, message: t('auth:validation.passwordRequired') },
          { min: 8, message: t('auth:validation.passwordMinLength') }
        ]}
      >
        <Input.Password
          prefix={<LockOutlined className="auth-modal__input-icon" />}
          placeholder={t('auth:placeholder.password')}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
        />
      </Form.Item>

      {password && (
        <PasswordStrengthIndicator password={password} />
      )}

      <Form.Item
        name="confirmPassword"
        label={t('auth:form.confirmPassword')}
        dependencies={['password']}
        rules={[
          { required: true, message: t('auth:validation.confirmPasswordRequired') },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve()
              }
              return Promise.reject(new Error(t('auth:validation.passwordMismatch')))
            },
          }),
        ]}
      >
        <Input.Password
          prefix={<LockOutlined className="auth-modal__input-icon" />}
          placeholder={t('auth:placeholder.confirmPassword')}
          autoComplete="new-password"
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          className="auth-modal__submit"
        >
          {t('auth:register.submit')}
        </Button>
      </Form.Item>

      <div className="auth-modal__link-wrap auth-modal__link-wrap--spaced">
        <Text type="secondary" className="auth-modal__text">
          {t('auth:register.hasAccount')}{' '}
          <Button
            type="link"
            onClick={onSwitchToLogin}
            className="auth-modal__link auth-modal__link--strong"
          >
            {t('auth:register.loginNow')}
          </Button>
        </Text>
      </div>
    </Form>
  )
}

export const RegisterModal: FC<RegisterModalProps> = ({
  visible,
  onClose,
  onSwitchToLogin
}) => {
  const { t } = useTranslation(['common', 'auth'])
  const { signUp, loading, error, clearError, user } = useAuth()
  const [registrationStep, setRegistrationStep] = useState<'form' | 'verification_sent'>('form')
  const [password, setPassword] = useState('')

  const handleSubmit = async (values: {
    email: string
    password: string
    confirmPassword: string
  }) => {
    const { success } = await signUp(values.email, values.password)
    if (success) {
      // 统一显示邮箱验证步骤，因为所有注册都需要验证
      setRegistrationStep('verification_sent')
    }
  }

  const handleCancel = () => {
    clearError()
    setRegistrationStep('form')
    setPassword('')
    onClose()
  }

  const handleComplete = () => {
    setRegistrationStep('form')
    setPassword('')
    onClose()
  }

  // 监听认证状态变化，当用户验证邮箱后自动关闭弹窗
  useEffect(() => {
    if (registrationStep === 'verification_sent' && user?.email_confirmed_at) {
      // 用户已验证邮箱，显示成功状态并关闭弹窗
      setTimeout(() => {
        setRegistrationStep('form')
        setPassword('')
        onClose()
      }, 1500) // 延迟1.5秒关闭，让用户看到成功状态
    }
  }, [registrationStep, user, onClose])

  // 监听弹窗的打开和关闭，确保状态正确重置
  useEffect(() => {
    if (!visible) {
      // 弹窗关闭时重置状态
      setRegistrationStep('form')
      setPassword('')
    }
  }, [visible])

  return (
    <Modal
      open={visible}
      onCancel={registrationStep === 'form' ? handleCancel : handleComplete}
      footer={null}
      width={registrationStep === 'form' ? 400 : 500}
      centered
      closable={registrationStep === 'form'}
      maskClosable={registrationStep === 'form'}
      closeIcon={registrationStep === 'form' ? <CloseOutlined className="auth-modal__close-icon" /> : null}
    >
      {/* 注册表单步骤 */}
      {registrationStep === 'form' && (
        <div>
          <div className="auth-modal__header">
            <Title level={2} className="auth-modal__title">
              {t('auth:register.title')}
            </Title>
            <Text type="secondary" className="auth-modal__subtitle">
              {t('auth:register.subtitle')}
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              className="auth-modal__alert"
              closable
              onClose={clearError}
            />
          )}

          <RegisterForm
            onSubmit={handleSubmit}
            loading={loading}
            password={password}
            setPassword={setPassword}
            onSwitchToLogin={onSwitchToLogin}
          />
        </div>
      )}

      {/* 邮箱验证发送成功步骤 */}
      {registrationStep === 'verification_sent' && (
        <Result
          icon={<MailTwoTone twoToneColor="#52c41a" />}
          status={user?.email_confirmed_at ? "success" : "info"}
          title={user?.email_confirmed_at ? t('auth:result.emailVerifiedTitle') : t('auth:success.register')}
          subTitle={user?.email_confirmed_at ? t('auth:result.emailVerifiedSubTitle') : t('auth:result.emailVerification')}
          extra={user?.email_confirmed_at ? [] : [
            <Button type="primary" key="complete" onClick={handleComplete}>
              {t('auth:button.gotIt')}
            </Button>,
          ]}
        />
      )}
    </Modal>
  )
}
